/**
 * Domain types — shared between Worker and Frontend.
 * Mirror Postgres schema 1:1 (snake_case stays snake_case).
 */

// === Enums ===
// MANAGER sits between HR and OWNER: same operational access as HR, but can
// approve above HR (incl. HR's own requests) and is a higher escalation target.
export type Role = 'OWNER' | 'MANAGER' | 'HR' | 'SUPERVISOR' | 'EMPLOYEE';
export const ROLES: readonly Role[] = ['OWNER', 'MANAGER', 'HR', 'SUPERVISOR', 'EMPLOYEE'] as const;

/** Authority rank — higher = more authority. Used by requireRole (minimum
 *  level) and the approval hierarchy (an approver must out-rank the target). */
export const ROLE_RANK: Record<Role, number> = {
  OWNER: 5,
  MANAGER: 4,
  HR: 3,
  SUPERVISOR: 2,
  EMPLOYEE: 1,
};

export type LeaveType = 'sick' | 'personal' | 'vacation' | 'unpaid' | 'maternity';
export const LEAVE_TYPES: readonly LeaveType[] = ['sick', 'personal', 'vacation', 'unpaid', 'maternity'] as const;

export type OTType = 'ot_1' | 'ot_2' | 'ot_3';
export const OT_TYPES: readonly OTType[] = ['ot_1', 'ot_2', 'ot_3'] as const;

export type LeaveStatus =
  | 'pending' | 'pending_L1' | 'pending_L2' | 'pending_L3'
  | 'info_requested' | 'conditionally_approved'
  | 'approved' | 'rejected';

export type OTStatus =
  | 'pending' | 'pending_L1' | 'pending_L2' | 'pending_L3'
  | 'approved' | 'rejected';

export type AttendanceStatus =
  | 'working' | 'short_work' | 'on_leave'
  | 'absent' | 'holiday' | 'over_quota';

export type EmployeeStatus = 'active' | 'probation' | 'resigned' | 'terminated';

export type TenantStatus = 'trial' | 'active' | 'suspended' | 'archived';

export type PayrollStatus =
  | 'draft'
  | 'previewed'
  | 'approved'
  | 'locked'
  | 'pdf_ready'
  | 'sending'
  | 'partial_sent'
  | 'sent'
  | 'voided';

export type EscalationType =
  | 'short_work' | 'ot_mismatch' | 'ot_unrequested'
  | 'absent_no_evidence' | 'over_leave_quota'
  | 'checkin_geofence_failed' | 'conditional_evidence_overdue';

// === Profile (from /me / /auth/pair) ===
export interface AuthProfile {
  user_id: string;        // UUID in users table; "platform" for tenant-less platform admins
  tenant_id: string | null;
  /** 6-char public tenant code (e.g. STHKJU) — Owner shares with staff for pairing */
  tenant_code?: string | null;
  tenant_slug: string | null;
  tenant_name: string;
  /** Latest tenant status as of /auth/me time — UI uses this to render
   *  the "pending approval" holding screen for fresh signups. */
  tenant_status?: 'pending_approval' | 'trial' | 'active' | 'suspended' | 'archived';
  emp_code: string | null;
  line_user_id: string;
  display_name: string | null;
  role: Role;
  /** True if this LINE userId is in env.PLATFORM_ADMIN_LINE_IDS allowlist. */
  is_platform_admin?: boolean;
  /** Operator email — set only for platform-admin (password) login. */
  email?: string | null;
  /** Tenant runs in solo mode (owner also does HR prep) — gates payroll UI actions. */
  solo_mode?: boolean;
}

// === Employee ===
export interface Employee {
  id: string;
  tenant_id: string;
  emp_code: string;
  first_name: string;
  last_name: string;
  nickname: string | null;
  email: string;
  phone: string | null;
  department: string;
  position: string;
  supervisor_email: string | null;
  start_date: string;      // ISO date YYYY-MM-DD
  end_date: string | null;
  status: EmployeeStatus;
  sso_number: string | null;
  bank: string | null;
  bank_branch: string | null;
  bank_account_masked: string | null;
  created_at: string;
  updated_at: string;
}

// === Leave ===
export interface LeaveRecord {
  id: string;
  tenant_id: string;
  leave_id: string;        // LV-YYYYMMDD-NNN
  emp_code: string;
  request_group_id: string;
  date: string;
  leave_type: LeaveType;
  reason: string | null;
  evidence_url: string | null;
  status: LeaveStatus;
  is_backdated: boolean;
  required_levels: 1 | 2 | 3;
  current_level: number;
  l1_approver: string | null;
  l1_decided_at: string | null;
  l2_approver: string | null;
  l2_decided_at: string | null;
  l3_approver: string | null;
  l3_decided_at: string | null;
  final_decided_at: string | null;
  rejection_reason: string | null;
  conditional_evidence_due: string | null;
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

// === OT ===
export interface OTRequest {
  id: string;
  tenant_id: string;
  ot_id: string;           // OT-YYYYMMDD-NNN
  emp_code: string;
  date: string;
  start_time: string;      // HH:mm
  end_time: string;
  ot_type: OTType;
  reason: string;
  status: OTStatus;
  required_levels: 1 | 2 | 3;
  current_level: number;
  l1_approver: string | null;
  l1_decided_at: string | null;
  l2_approver: string | null;
  l2_decided_at: string | null;
  l3_approver: string | null;
  l3_decided_at: string | null;
  final_decided_at: string | null;
  rejection_reason: string | null;
  submitted_at: string;
}

// === Quota ===
export interface LeaveQuota {
  emp_code: string;
  year: number;
  sick_quota: number;
  sick_used: number;        // computed at query time
  personal_quota: number;
  personal_used: number;
  vacation_quota: number;
  vacation_used: number;
}

// === Check-in ===
import type { CheckinKind } from '../schemas/index.js';

export interface CheckinEvent {
  id: string;
  emp_code: string;
  date: string;             // YYYY-MM-DD
  kind: CheckinKind;
  captured_at: string;      // ISO timestamp
  selfie_url: string | null; // Worker-signed URL (resolveFileUrl on client)
  latitude: number | null;
  longitude: number | null;
  geofence_passed: boolean | null;
  approval_status: 'auto_ok' | 'pending_review' | 'approved' | 'rejected';
}

/** Daily roll-up returned by GET /checkin/status — one entry per kind. */
export interface CheckinDayStatus {
  date: string;
  events: Partial<Record<CheckinKind, CheckinEvent>>;
}

// === Slip ===
export interface PayrollSlip {
  id: string;
  emp_code: string;
  period: string;          // YYYY-MM
  base_pay: number;
  ot_1_pay: number;
  ot_2_pay: number;
  ot_3_pay: number;
  additions_total: number;
  gross: number;
  tax_amount: number;
  sso_amount: number;
  studentloan_amount: number;
  companyloan_amount: number;
  pf_amount: number;
  manual_deductions: number;
  deductions_total: number;
  net: number;
  status: PayrollStatus;
  sent_at: string | null;
  slip_pdf_url: string | null;   // presigned R2 URL
}

// === Monthly summary (Public/Secret bridge — no money) ===
export interface MonthlySummary {
  emp_code: string;
  period: string;
  working_days: number;
  short_days: number;
  paid_leave_days: number;
  unpaid_leave_days: number;
  absent_days: number;
  holiday_days: number;
  ot_1_hours: number;
  ot_2_hours: number;
  ot_3_hours: number;
  has_unresolved: boolean;
  locked: boolean;
}

// === Escalation ===
export interface Escalation {
  id: string;
  esc_id: string;
  emp_code: string;
  date: string;
  type: EscalationType;
  detail: string | null;
  status: 'pending' | 'resolved' | 'ignored';
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
}

// === API envelope ===
export type ApiResponse<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code: string; details?: unknown };

export * from './doc-kit.js';
